import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { compact, includes, isNil } from 'lodash'
import ExportFileModal from '../../../../components/modal/ExportFileModal'
import ExportAction from '../../../../export/actions/ExportAction'
import { STATION_TYPE_NAME } from '../../../../station/constants/StationConstants'
import ToastrAction from '../../../../toastr/actions/ToastrAction'
import useListIndexed from '../../../../utils/customHook/useListIndexed'
import { getDate } from '../../../../utils/DateUtil'
import { formatData } from '../../../../utils/ExportDataUtil'
import { getPlanningProducersName, getStationPlanningObjectiveIds } from '../../../utils/CampaignUtils'
import { StationCampaignContext } from '../DashboardCampaign'
import { useFormattedObjectives, useFormattedStations } from './tab/PopinCampaignPlanning'

const MODEL_STATION_TYPES = [STATION_TYPE_NAME.piezometry, STATION_TYPE_NAME.hydrometry, STATION_TYPE_NAME.pluviometry]
const PLANNING_EXPORT_HEADERS = ['code', 'city', 'name', 'objective']

const StationCampaignExportPopup = ({
    onClose = () => {},
    onExportCampaign = () => {},
    planningId,
}) => {
    const {
        stationType,
        campaign,
        campaignPlannings,
        campaignPlanningsExceptions,
    } = useContext(StationCampaignContext)

    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const dispatch = useDispatch()

    const formattedStations = useFormattedStations()
    const indexedObjectives = useListIndexed(useFormattedObjectives(), 'id')

    const [models, setModels] = useState([])

    // les modèles lisent la base : une planification pas encore enregistrée (id négatif) n'y existe pas
    const canExportModels = includes(MODEL_STATION_TYPES, stationType) && (isNil(planningId) || planningId > 0)

    useEffect(() => {
        if (canExportModels) {
            dispatch(ExportAction.getEnvironmentModelsByType(`${stationType}Campaign`)).then(res => setModels(res || []))
        }
    }, [dispatch, stationType, canExportModels])

    const onExportPlanning = (exportType) => {
        const planning = campaignPlannings.find(({ id }) => id === planningId)
        const planningExceptions = campaignPlanningsExceptions.filter(exception => exception.planningId === planningId)
        const rows = formattedStations
            .filter(({ id }) => includes(planning.stationsPlan, id))
            .flatMap(station => {
                const stationCells = {
                    code: station.code,
                    city: station.city?.value,
                    name: station.name?.value,
                }
                const objectiveNames = compact(getStationPlanningObjectiveIds(planning, planningExceptions, station.id)
                    .map(objectiveId => indexedObjectives[objectiveId]?.name))
                return objectiveNames.length
                    ? objectiveNames.map(objective => ({ ...stationCells, objective }))
                    : [stationCells]
            })

        if (!rows.length) {
            dispatch(ToastrAction.warning(i18n.noDataToExport))
            return
        }

        const producerName = getPlanningProducersName(planning, contacts).replaceAll(', ', '-') // pas de virgule dans le nom du fichier
        const fileName = compact([
            i18n.planning,
            getDate(planning.startDate, 'DD-MM-YYYY'),
            getDate(planning.endDate, 'DD-MM-YYYY'),
            producerName,
        ]).join('_')

        const dataWithHeaders = [{ ...rows[0], headers: PLANNING_EXPORT_HEADERS }, ...rows.slice(1)]
        dispatch(ExportAction.export(formatData(dataWithHeaders), exportType, fileName))
    }

    const onExportFull = exportType => isNil(planningId) ? onExportCampaign(exportType) : onExportPlanning(exportType)

    const fullExport = {
        name: i18n.exportFull,
        formats: [{
            type: i18n.exportXLSX,
            callback: () => onExportFull('xlsx'),
        }, {
            type: i18n.csv,
            callback: () => onExportFull('csv'),
        }],
    }

    const hydroObsExport = stationType === STATION_TYPE_NAME.hydrometry && isNil(planningId) ? [{
        name: i18n.flowObservations,
        formats: [{
            type: i18n.exportXLSX,
            callback: () => dispatch(ExportAction.exportHydroObsCampaign({ campaignId: campaign.id, campaignName: campaign.name, exportType: 'xlsx' })),
        }, {
            type: i18n.csv,
            callback: () => dispatch(ExportAction.exportHydroObsCampaign({ campaignId: campaign.id, campaignName: campaign.name, exportType: 'csv' })),
        }],
    }] : []

    const modelExports = canExportModels ? models.map(model => ({
        name: model,
        formats: [{
            type: i18n.excelFile,
            callback: () => dispatch(ExportAction.exportCampaignModel({ campaignId: campaign.id, planningId, stationType }, model)),
        }],
    })) : []

    return (
        <ExportFileModal
            open
            onClose={onClose}
            data={[fullExport, ...hydroObsExport, ...modelExports]}
            closeOnExport
        />
    )
}

StationCampaignExportPopup.propTypes = {
    onClose: PropTypes.func,
    onExportCampaign: PropTypes.func,
    planningId: PropTypes.number,
}

export default StationCampaignExportPopup
